import {
  AppBar,
  Avatar,
  FormControlLabel,
  Link,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import {
  isAdmin,
  isOrgActive,
  isOrgUser,
  isSandbox,
  isSsoUser,
  setSandbox,
  userIsLoggedIn,
} from "../utils/io";

const propTypes = {
  onLogout: PropTypes.func,
  sandboxFlag: PropTypes.bool,
};

const defaultProps = {
  onLogout: () => {},
  sandboxFlag: false,
};

function Header({ onLogout, sandboxFlag }) {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState();

  if (userIsLoggedIn()) {
    const onDropdownClosed = () => {
      setAnchorEl();
    };

    const onLogoutSelected = () => {
      setAnchorEl();
      onLogout();
      history.push("/login");
    };

    const switchSandbox = (isOn) => {
      setSandbox(isOn);
      window.location.reload();
    };

    return (
      <AppBar>
        <Toolbar>
          <Typography variant="h6">Stel Life Management</Typography>
          <Link
            component={RouterLink}
            to="/hubs"
            sx={{
              color: theme.palette.background.paper,
              margin: theme.spacing(0, 0, 0, 2),
              textDecoration:
                location.pathname === "/hubs" ? undefined : "none",
              "&:hover": {
                textDecoration:
                  location.pathname === "/hubs" ? undefined : "underline",
              },
            }}
          >
            Hubs
          </Link>
          <Link
            component={RouterLink}
            to="/measures"
            sx={{
              color: theme.palette.background.paper,
              margin: theme.spacing(0, 0, 0, 2),
              textDecoration:
                location.pathname === "/measures" ? undefined : "none",
              "&:hover": {
                textDecoration:
                  location.pathname === "/measures" ? undefined : "underline",
              },
            }}
          >
            Measures
          </Link>
          {isOrgUser() && (
            <Link
              component={RouterLink}
              to="/hub-group"
              sx={{
                color: theme.palette.background.paper,
                margin: theme.spacing(0, 0, 0, 2),
                textDecoration:
                  location.pathname === "/hub-group" ? undefined : "none",
                "&:hover": {
                  textDecoration:
                    location.pathname === "/hub-group"
                      ? undefined
                      : "underline",
                },
              }}
            >
              Hub Groups
            </Link>
          )}

          {isOrgUser() && (
            <Link
              component={RouterLink}
              to="/endpoints"
              sx={{
                color: theme.palette.background.paper,
                margin: theme.spacing(0, 0, 0, 2),
                textDecoration:
                  location.pathname === "/endpoints" ? undefined : "none",
                "&:hover": {
                  textDecoration:
                    location.pathname === "/endpoints"
                      ? undefined
                      : "underline",
                },
              }}
            >
              Endpoints
            </Link>
          )}
          {isOrgUser() && (
            <Link
              component={RouterLink}
              to="/users"
              sx={{
                color: theme.palette.background.paper,
                margin: theme.spacing(0, 0, 0, 2),
                textDecoration:
                  location.pathname === "/users" ? undefined : "none",
                "&:hover": {
                  textDecoration:
                    location.pathname === "/users" ? undefined : "underline",
                },
              }}
            >
              Users
            </Link>
          )}
          {isAdmin() && (
            <Link
              component={RouterLink}
              to="/orders"
              sx={{
                color: theme.palette.background.paper,
                margin: theme.spacing(0, 0, 0, 2),
                textDecoration:
                  location.pathname === "/orders" ? undefined : "none",
                "&:hover": {
                  textDecoration:
                    location.pathname === "/orders" ? undefined : "underline",
                },
              }}
            >
              Orders
            </Link>
          )}

          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginLeft: "auto" }}
          >
            {isOrgUser() && (
              <>
                {isSandbox() && (
                  <Link
                    component={RouterLink}
                    to="/simulate-measurements"
                    sx={{
                      color: theme.palette.background.paper,
                      margin: theme.spacing(0, 0, 0, 2),
                      textDecoration:
                        location.pathname === "/simulate-measurements"
                          ? undefined
                          : "none",
                      "&:hover": {
                        textDecoration:
                          location.pathname === "/simulate-measurements"
                            ? undefined
                            : "underline",
                      },
                    }}
                  >
                    Simulate Measures
                  </Link>
                )}
                <Link
                  href="https://stel.wiki"
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    color: theme.palette.background.paper,
                    margin: theme.spacing(0, 0, 0, 2),
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Typography>Docs</Typography>
                </Link>
                <Tooltip
                  sx={{ tooltip: { fontSize: "13px" } }}
                  title="Contact sales@stel.life to get started in production."
                  enterDelay={10}
                >
                  <FormControlLabel
                    labelPlacement="start"
                    sx={{
                      marginRight: "10px",
                      padding: "4px",
                      paddingLeft: "11px",
                      color: theme.palette.common.black,
                      backgroundColor: theme.palette.grey["100"],
                      borderRadius: "8px",
                    }}
                    checked={sandboxFlag}
                    onChange={(e) => switchSandbox(e.target.checked)}
                    control={<Switch color="default" />}
                    label={`Sandbox ${sandboxFlag ? "On" : "Off"}`}
                    disabled={!isOrgActive()}
                  />
                </Tooltip>
              </>
            )}

            <Avatar
              sx={{
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                cursor: "pointer",
                margin: theme.spacing(0, 0, 0, 2),
              }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            />
          </Stack>

          <Menu
            id="header-user-dropdown"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            getContentAnchorEl={null}
            onClose={onDropdownClosed}
            open={Boolean(anchorEl)}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            {!isSsoUser() && isOrgUser() && (
              <MenuItem onClick={() => history.push("/change-password")}>
                Change Password
              </MenuItem>
            )}
            <MenuItem onClick={onLogoutSelected}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <AppBar>
      <Toolbar>
        <Typography variant="h6">Stel Life Management</Typography>
        <Link
          href="https://stel.wiki"
          target="_blank"
          rel="noreferrer"
          sx={{
            color: theme.palette.background.paper,
            margin: theme.spacing(0, 0, 0, 2),
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
            marginLeft: "auto",
          }}
        >
          <Typography>Docs</Typography>
        </Link>
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;

export default Header;
